"use client";

import { useEffect, useState } from "react";

type RadarAxis = {
  label: string;
  value: number; // 0–10
};

const MAX = 10;
const SIZE = 280;
const CENTER = SIZE / 2;
const RADIUS = 110;
const LABEL_RADIUS = RADIUS + 26;
// Extra horizontal padding in the viewBox so long wrapped labels on the
// left/right sides ("Cost Efficiency", "Portfolio Quality", etc.) are not
// clipped. Padding is baked into the viewBox so the chart geometry stays
// centered around CENTER.
const VIEW_PAD_X = 50;
const VIEW_PAD_Y = 6;
const GRID_LEVELS = [2, 4, 6, 8, 10]; // concentric rings

function polarToXY(angle: number, radius: number): [number, number] {
  // Start from top (−π/2) and go clockwise
  const rad = (angle - 90) * (Math.PI / 180);
  return [CENTER + radius * Math.cos(rad), CENTER + radius * Math.sin(rad)];
}

function getAxisAngle(index: number, total: number): number {
  return (360 / total) * index;
}

function polygonPoints(axes: RadarAxis[], scale: number): string {
  return axes
    .map((a, i) => {
      const angle = getAxisAngle(i, axes.length);
      const r = (a.value / MAX) * RADIUS * scale;
      const [x, y] = polarToXY(angle, r);
      return `${x},${y}`;
    })
    .join(" ");
}

function gridPolygon(level: number, count: number): string {
  const r = (level / MAX) * RADIUS;
  return Array.from({ length: count })
    .map((_, i) => {
      const angle = getAxisAngle(i, count);
      const [x, y] = polarToXY(angle, r);
      return `${x},${y}`;
    })
    .join(" ");
}

function scoreColor(score: number): string {
  if (score >= 8) return "#10B981";
  if (score >= 6) return "#3B82F6";
  if (score >= 4) return "#F59E0B";
  return "#EF4444";
}

export function RadarChart({ axes }: { axes: RadarAxis[] }) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    // Trigger animation on next frame
    const raf = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const count = axes.length;

  const viewW = SIZE + VIEW_PAD_X * 2;
  const viewH = SIZE + VIEW_PAD_Y * 2;

  return (
    <div className="flex justify-center">
      <svg
        viewBox={`${-VIEW_PAD_X} ${-VIEW_PAD_Y} ${viewW} ${viewH}`}
        width={viewW}
        height={viewH}
        className="select-none max-w-full h-auto"
      >
        {/* Grid rings */}
        {GRID_LEVELS.map((level) => (
          <polygon
            key={`grid-${level}`}
            points={gridPolygon(level, count)}
            fill="none"
            stroke="rgba(148,163,184,0.12)"
            strokeWidth={level === 10 ? 1 : 0.5}
          />
        ))}

        {/* Axis lines */}
        {axes.map((_, i) => {
          const angle = getAxisAngle(i, count);
          const [x, y] = polarToXY(angle, RADIUS);
          return (
            <line
              key={`axis-${i}`}
              x1={CENTER}
              y1={CENTER}
              x2={x}
              y2={y}
              stroke="rgba(148,163,184,0.15)"
              strokeWidth={0.5}
            />
          );
        })}

        {/* Data polygon */}
        <polygon
          points={animated ? polygonPoints(axes, 1) : polygonPoints(axes, 0)}
          fill="rgba(59,130,246,0.3)"
          stroke="#3B82F6"
          strokeWidth={2}
          strokeLinejoin="round"
          style={{
            transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        />

        {/* Data points + score values */}
        {axes.map((a, i) => {
          const angle = getAxisAngle(i, count);
          const r = animated ? (a.value / MAX) * RADIUS : 0;
          const [x, y] = polarToXY(angle, r);
          const [labelX, labelY] = polarToXY(angle, LABEL_RADIUS);
          const [scoreX, scoreY] = polarToXY(angle, r + 14);

          return (
            <g key={`point-${i}`}>
              {/* Dot on polygon vertex */}
              <circle
                cx={x}
                cy={y}
                r={3.5}
                fill={scoreColor(a.value)}
                stroke="#0A0E1A"
                strokeWidth={1.5}
                style={{
                  transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)",
                }}
              />
              {/* Score value near vertex */}
              <text
                x={scoreX}
                y={scoreY}
                textAnchor="middle"
                dominantBaseline="central"
                fill={scoreColor(a.value)}
                fontSize={11}
                fontWeight={700}
                fontFamily="DM Sans, sans-serif"
                style={{
                  transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  opacity: animated ? 1 : 0,
                }}
              >
                {a.value}
              </text>
              {/* Axis label outside */}
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#94A3B8"
                fontSize={9.5}
                fontWeight={500}
                fontFamily="DM Sans, sans-serif"
              >
                {wrapLabel(a.label, labelX)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Split long labels into two <tspan> lines
function wrapLabel(label: string, x: number) {
  const words = label.split(" ");
  if (words.length <= 1 || label.length <= 10) {
    return label;
  }
  // Split roughly in half
  const mid = Math.ceil(words.length / 2);
  const line1 = words.slice(0, mid).join(" ");
  const line2 = words.slice(mid).join(" ");
  return (
    <>
      <tspan x={x} dy="-0.5em">{line1}</tspan>
      <tspan x={x} dy="1.15em">{line2}</tspan>
    </>
  );
}
